import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { QuoteStatus } from 'src/app/models/quote/quote-status';
import { RecommendationStatus } from 'src/app/models/recommendation/recommendation-status';
import { RecommendationUserDefinedStatus } from 'src/app/models/recommendation/recommendation-user-defined-status';
import { PostSearch } from 'src/app/models/search/post-search';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { UserService } from 'src/app/services/user/user.service';
import { Geolocation } from '@capacitor/geolocation';
import { PostFieldFromGpsGet } from 'src/app/models/search/post-field-from-gps-get';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { forkJoin } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { SearchResults } from 'src/app/models/search/search-results';

@Component({
  selector: 'app-search',
  templateUrl: './search.page.html',
  styleUrls: ['./search.page.scss', '../../../shared-styles/barcode-scanner.scss'],
})
export class SearchPage implements OnInit, OnDestroy {

  title: string = 'Search';
  recommendationStatuses: RecommendationStatus[] = [];
  recommendationUserDefinedStatus: RecommendationUserDefinedStatus = <RecommendationUserDefinedStatus>{ description: 'Custom Status' };
  quoteStatuses: QuoteStatus[] = [];
  postSearch: PostSearch = <PostSearch>{};
  isMobilePlatform: boolean;
  scanActive: boolean;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private toastWrapper: ToastControllerWrapperService,
    private loadingWrapper: LoadingControllerWrapperService,
    private userService: UserService,
    private userData: UserDataService
  ) { }

  async ngOnInit() {
    this.isMobilePlatform = this.userData.isMobilePlatform();
    this.postSearch = <PostSearch>{};

    await this.loadingWrapper.presentLoading('');
    forkJoin([
      this.route.snapshot.data.recommendationStatus,
      this.route.snapshot.data.quoteStatus,
      this.route.snapshot.data.recommendationUserDefinedStatus
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([recommendationStatus, quoteStatus, recommendationUserDefinedStatus]: [RecommendationStatus[], QuoteStatus[], RecommendationUserDefinedStatus]) => {
        this.recommendationStatuses = recommendationStatus,
          this.quoteStatuses = quoteStatus,
          this.recommendationUserDefinedStatus = recommendationUserDefinedStatus
      });
  }

  ionViewWillEnter() {
    this.titleService.setTitle(this.title);
  }
  
  ngOnDestroy() {
    this.stopScanner();
  }

  async submitSearch() {

    await this.toastWrapper.dismissToast();
    await this.loadingWrapper.presentLoading('');

    this.userService.search(this.postSearch)
      .pipe(finalize(async () => {
        await this.loadingWrapper.dismissLoading();
      }))
      .subscribe(
        async response => {
          if (response.success) {
            if (this.resultsFound(response.payload)) {
              this.router.navigate(['/search-results'], { state: response.payload })
            }
            else {
              await this.toastWrapper.presentErrorToast('There were no search results.');
            }
          }
        }
      );
  }

  resultsFound(results: SearchResults) {
    return results.exisitingCustomerResults.length > 0
      || results.existingEquipmentResults.length > 0
      || results.prospectCustomerResults.length > 0
      || results.quoteResults.length > 0
      || results.recommendationEquipmentResults.length > 0
      || results.recommendationResults.length > 0
      || results.surveyCustomerResults.length > 0
      || results.surveyEquipmentResults.length > 0;
  }

  async triggerGpsPopulation() {
    try {

      await this.loadingWrapper.presentLoading('');

      const position = await Geolocation.getCurrentPosition();
      let coordinates = <PostFieldFromGpsGet>{
        longitude: position.coords.longitude,
        latitude: position.coords.latitude
      };

      this.userService.getFieldsFromGps(coordinates)
        .pipe(finalize(async () => {
          await this.loadingWrapper.dismissLoading();
        }))
        .subscribe(
          async response => {
            if (response.success) {
              this.postSearch.city = response.payload.city;
              this.postSearch.street = response.payload.street;
              this.postSearch.street_number = response.payload.streetNumber;
            }
            else {
              await this.toastWrapper.presentErrorToast(response.message);
            }
          }
        );

    } catch (error) {
      await this.loadingWrapper.dismissLoading();
      this.toastWrapper.presentErrorToast('Cannot get your current location.');
    }

  }

  async triggerBarcodeScanner() {
    if (this.isMobilePlatform) {
      const allowed = await BarcodeScanner.checkPermission({ force: true });
      if (allowed.granted) {
        this.scanActive = true;
        await BarcodeScanner.hideBackground();
        const result = await BarcodeScanner.startScan();
        if (result.hasContent) {
          this.postSearch.serial_number = result.content;
          this.scanActive = false;
        }
      }
    }
  }

  stopScanner() {
    if (this.isMobilePlatform) {
      BarcodeScanner.stopScan().catch(reason => {
        console.log("Stopping barcode scanner: ", reason);
      });
    }
    this.scanActive = false;
  }
}
